import { CheckedIconWithoutBg, SquareDownArrow, SquareUpArrow } from 'assests/GmsIcons';
import React, { useMemo, useState } from 'react'
import { attributeActions } from '../utils/Constants';
import CommonAttributeValues from './CommonAttributeValues';
import UniqueAttributeValues from './UniqueAttributeValues';
import NoDataComponent from 'Components/Common/NoDataComponent';
import { getCommonAttributeValues } from '../services/RecategorizeServices';
import Select, { components } from "react-select";
import { customStyles } from '../utils/Styles';

type SourceUniqueAttributesTableProps = {
    payload: TRecategorizePayload;
    setPayload: React.Dispatch<React.SetStateAction<TRecategorizePayload>>;
    recategorizationResponse: RecategorizationResponse | undefined;
    setAttributeValueData: React.Dispatch<React.SetStateAction<AttributeValueMapping | null>>;
}

const SourceUniqueAttributesTable = ({ payload, setPayload, recategorizationResponse, setAttributeValueData }: SourceUniqueAttributesTableProps) => {
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set(['variant']));
    const [checkedRows, setCheckedRows] = useState<Set<string>>(new Set(payload.unique_attr_source_list.filter(row => row?.checked).map(row => row?.attribute_id)));

    const getCommonValues = (sourceAttributeId: string) => {
        const sourceAttr = payload.unique_attr_source_list.find(
            attr => attr.attribute_id === sourceAttributeId
        );
        return sourceAttr?.common_attribute_values ?? [];
    };

    const getUniqueAttributeValues = (
        sourceAttrValues: AttributeValue[],
        commonValues: string[]
    ): AttributeValue[] => {
        return sourceAttrValues.filter(item => !commonValues.includes(item.value));
    };

    const commonAttributeValues = (sourceAttributeId: string): string[] => {
        const sourceAttr = payload?.unique_attr_source_list?.find(attr => attr.attribute_id === sourceAttributeId);
        return sourceAttr?.common_attribute_values ?? [];
    };

    const sourceAttributeValues = (sourceAttributeId: string): AttributeValue[] => {
        return recategorizationResponse?.unique_attr_source_list?.filter((e) => e.attribute_id === sourceAttributeId)[0]?.attribute_value ?? [];
    }

    const toggleExpand = (id: string): void => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const toggleCheck = (id: string): void => {
        setCheckedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });

        if (recategorizationResponse?.unique_attr_source_list) {
            const updatedAttrList = recategorizationResponse.unique_attr_source_list.map(attr => {
                const existingAttr = payload.unique_attr_source_list.find(
                    existing => existing.attribute_id === attr.attribute_id
                );

                const isClickedItem = attr.attribute_id === id;
                const isUnchecking = checkedRows.has(id);

                return {
                    ...existingAttr,
                    attribute_id: attr.attribute_id,
                    attribute_name: attr.attribute_name,
                    checked: isClickedItem ? !isUnchecking : checkedRows.has(attr.attribute_id),
                    action: isClickedItem && isUnchecking ? null : (existingAttr?.action),
                    target_attribute_id: isClickedItem && isUnchecking ? undefined : existingAttr?.target_attribute_id,
                    target_attribute_name: isClickedItem && isUnchecking ? undefined : existingAttr?.target_attribute_name,
                } as UniqueAttributePayload;
            });

            setPayload(prev => ({
                ...prev,
                unique_attr_source_list: updatedAttrList
            }));
        }
    };

    const handleActionChange = (attributeId: string, action: AttributeActionType) => {
        setPayload(prev => {
            const updatedUniqueAttrList = prev.unique_attr_source_list.map(attr =>
                attr.attribute_id === attributeId
                    ? {
                        ...attr,
                        action,
                        target_attribute_id: undefined,
                        target_attribute_name: undefined,
                        unique_attribute_values: [],
                        common_attribute_values: []
                    }
                    : attr
            );

            return {
                ...prev,
                unique_attr_source_list: updatedUniqueAttrList
            };
        });
    };

    const handleTargetAttributeChange = async (
        attributeId: string,
        targetAttributeId: string,
        targetAttributeName: string
    ) => {
        const sourceAttrValues = sourceAttributeValues(attributeId);
        const commonAttributeValuesResponse = await fetchCommonAttributeValues(
            targetAttributeId,
            sourceAttrValues
        );
        const commonValues = commonAttributeValuesResponse?.map((e: AttributeValue) => e.value) || [];

        setPayload(prev => {
            const updatedUniqueAttrList = prev.unique_attr_source_list.map(attr => {
                if (attr.attribute_id !== attributeId) return attr;

                const uniqueValues = getUniqueAttributeValues(sourceAttrValues, commonValues)
                    .map(val => ({
                        attribute_value: val.value,
                        action: undefined
                    }));

                return {
                    ...attr,
                    target_attribute_id: targetAttributeId,
                    target_attribute_name: targetAttributeName,
                    unique_attribute_values: uniqueValues,
                    common_attribute_values: commonValues
                };
            });

            return {
                ...prev,
                unique_attr_source_list: updatedUniqueAttrList
            };
        });
    };

    const fetchCommonAttributeValues = async (targetAttributeId: string, attributeValueList: AttributeValue[]) => {
        const attributeValues = attributeValueList.map(attrValue => attrValue.value);
        const formattedAttributeValues = JSON.stringify(attributeValues);
        try {
            const response = await getCommonAttributeValues(targetAttributeId, formattedAttributeValues);
            return response.data?.common_attribute_val ?? [];
        } catch (error) {
            console.error("Error fetching common attribute values:", error);
        }

    }

    const targetAttributeOptions = (
        currentAttributeId: string,
        targetUniqueAttributeList: Attribute[],
        existingSelection: UniqueAttributePayload[]
    ) => {
        const compareIds = new Set(
            existingSelection
                .filter(item => item.attribute_id !== currentAttributeId)
                .map(item => item.target_attribute_id)
        )

        return targetUniqueAttributeList
            .filter(attr => !compareIds.has(attr.attribute_id))
            .map(attr => ({
                value: attr.attribute_id,
                label: attr.attribute_name
            }))
    }

    const actionTypeSelectProps = useMemo(() => ({
        id: "actionTypeId",
        className: "tw-outline-none tw-w-full",
        classNamePrefix: "select",
        styles: {
            ...customStyles,
            loadingIndicator: (base: any) => ({
                ...base,
                color: '#0747a6'
            })
        },
        isSearchable: false,
        maxMenuHeight: 220,
        menuPlacement: "auto" as const,
        menuPosition: "absolute" as const,
        isClearable: false,
        placeholder: "Select"
    }), []);

    const selectProps = useMemo(() => ({
        id: "attributeId",
        className: "tw-outline-none tw-w-full",
        classNamePrefix: "select",
        styles: {
            ...customStyles,
            loadingIndicator: (base: any) => ({
                ...base,
                color: '#0747a6'
            })
        },
        isSearchable: true,
        maxMenuHeight: 220,
        menuPlacement: "auto" as const,
        menuPosition: "absolute" as const,
        isClearable: false,
        placeholder: "Select Attribute"
    }), []);

    if (!payload?.unique_attr_source_list || payload.unique_attr_source_list.length === 0) {
        return <NoDataComponent />
    }

    return (
        <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-bg-white tw-mt-3">
            <div className="tw-flex tw-flex-col">
                <table className="tw-w-full">
                    <thead className="tw-bg-[#F4F5F7] tw-rounded-t-lg tw-justify-center">
                        <tr className="tw-border-[1px] tw-border-[#DADBDD] tw-rounded-t-lg">
                            <th className="tw-w-[10%]  tw-pl-4 tw-py-3 tw-rounded-tl-lg"></th>
                            <th className="tw-w-[30%] tw-py-3 tw-text-left tw-font-semibold tw-text-sm tw-text-[#6D788C]">{payload?.source_category_name}</th>
                            <th className="tw-w-[24%] tw-py-3 tw-text-left tw-font-semibold tw-text-sm tw-text-[#6D788C]">Action</th>
                            <th className="tw-w-[35%] tw-py-3 tw-text-left tw-font-semibold tw-text-sm tw-text-[#6D788C]">{payload?.target_category_name}</th>
                            <th className="tw-w-[1%] tw-pr-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {payload.unique_attr_source_list.map((row, index) => (
                            <React.Fragment key={row.attribute_id}>
                                <tr className={`tw-border tw-border-[#DADBDD] ${index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-[#F4F5F7]'}`}>
                                    <td className="tw-w-12 tw-pl-4 tw-py-2">
                                        <label className="tw-relative tw-flex tw-items-center tw-justify-center">
                                            <input
                                                type="checkbox"
                                                checked={checkedRows.has(row.attribute_id)}
                                                onChange={() => toggleCheck(row.attribute_id)}
                                                className="tw-hidden"
                                            />
                                            <div className={`tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-border-2 tw-rounded-[4px] tw-cursor-pointer tw-transition-all tw-duration-200 
                                                ${checkedRows.has(row.attribute_id) ? 'tw-bg-[#091CAA] tw-border-[#091CAA]' : 'tw-border-[#091CAA] hover:tw-border-[#091CAA]'}`}>
                                                {checkedRows.has(row.attribute_id) && <CheckedIconWithoutBg />}
                                            </div>
                                        </label>
                                    </td>
                                    <td className="tw-py-2 tw-text-[#172B4D] tw-text-sm">{row.attribute_name}</td>
                                    <td className="tw-py-2 tw-text-[#172B4D] tw-text-sm tw-pr-2">
                                        <div className='tw-w-28'>
                                            <Select<OptionType>
                                                {...actionTypeSelectProps}
                                                options={attributeActions}
                                                isSearchable={false}
                                                onChange={(value) => handleActionChange(row.attribute_id, value?.value as AttributeActionType)}
                                                value={!row?.checked || !row?.action ? null : { "label": attributeActions.find(x => x.value === row?.action)?.label as AttributeActionLabelType, "value": row?.action }}
                                                components={{ IndicatorSeparator: () => null }}
                                                isDisabled={!row?.checked}
                                            />
                                        </div>
                                    </td>
                                    <td className="tw-py-2 tw-text-[#172B4D] tw-text-sm">
                                        {row?.action == null || !row?.checked ? <div className='tw-w-44'>NA</div> : row?.action === 'map' ? (
                                            <div title={row?.target_attribute_name ?? ''} className="tw-delay-0 tw-duration-0 tw-w-44">
                                                <Select<OptionType>
                                                    {...selectProps}
                                                    options={targetAttributeOptions(
                                                        row.attribute_id,
                                                        recategorizationResponse?.unique_attr_target_list ?? [],
                                                        payload?.unique_attr_source_list.filter((e) =>
                                                            e?.action === 'map' && e.target_attribute_id != null
                                                        )
                                                    )}
                                                    onChange={(value) => handleTargetAttributeChange(
                                                        row.attribute_id,
                                                        value?.value ?? index.toString(),
                                                        value?.label ?? index.toString()
                                                    )}
                                                    value={row?.target_attribute_id != null && row?.target_attribute_name != null
                                                        ? { "label": row.target_attribute_name, "value": row?.target_attribute_id }
                                                        : undefined
                                                    }
                                                    components={{
                                                        Option: (props) => (
                                                            <components.Option {...props}>
                                                                <div className="tw-truncate" title={props.children?.toString()}>
                                                                    {props.children}
                                                                </div>
                                                            </components.Option>
                                                        ),
                                                        IndicatorSeparator: () => null,
                                                        NoOptionsMessage: () => (
                                                            <div className="tw-p-2 tw-text-center tw-text-gray-500">
                                                                No attribute available
                                                            </div>
                                                        )
                                                    }}
                                                />
                                            </div>
                                        ) :
                                            (<div className="tw-w-44">
                                                {row.attribute_name}</div>
                                            )}
                                    </td>
                                    {row && (
                                        <td className="tw-w-12 tw-px-4">
                                            <button
                                                onClick={() => toggleExpand(row.attribute_id)}
                                                className="tw-bg-[#F4F5F7] tw-rounded hover:tw-bg-gray-100">
                                                {expandedRows.has(row.attribute_id) ? <SquareUpArrow /> : <SquareDownArrow />}
                                            </button>
                                        </td>
                                    )}
                                </tr>
                                {row?.action === 'transfer' && row?.attribute_id != null && expandedRows.has(row.attribute_id) && (
                                    <tr className="tw-border tw-border-[#DADBDD]">
                                        <td colSpan={4} className="tw-px-6 tw-py-2">
                                            {sourceAttributeValues(row?.attribute_id)?.length ? (
                                                <div>
                                                    <p className="tw-text-[#97A0AF] tw-font-semibold tw-text-xs">Attribute Values</p>
                                                    <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mt-2">
                                                        {sourceAttributeValues(row?.attribute_id).map((value) => (
                                                            <span key={value.id} className="tw-px-2 tw-py-1 tw-bg-[#F4F5F7] tw-rounded-sm tw-text-xs tw-text-[#172B4D]">
                                                                {value.value}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="tw-text-gray-500">No attribute values available</div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                                {row?.action === 'map' && row?.attribute_id != null && expandedRows.has(row.attribute_id) && (
                                    <tr className={`tw-border tw-border-[#DADBDD] ${index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-[#F4F5F7]'}`}>
                                        <td colSpan={5} className="tw-px-6 tw-py-4 tw-w-full">
                                            <div className="tw-w-full tw-flex tw-flex-col tw-divide-y tw-divide-[#DADBDD]">
                                                {row?.action === 'map' && row?.target_attribute_id != null && (
                                                    <div className="tw-w-full tw-pb-4">
                                                        <CommonAttributeValues attributeValues={commonAttributeValues(row?.attribute_id)} />
                                                    </div>
                                                )}

                                                {row?.action === 'map' && row?.target_attribute_id != null && (
                                                    <div className="tw-w-full tw-py-4">
                                                        <UniqueAttributeValues
                                                            attributeValues={getUniqueAttributeValues(
                                                                sourceAttributeValues(row.attribute_id),
                                                                getCommonValues(row.attribute_id)
                                                            )}
                                                            setAttributeValueData={setAttributeValueData}
                                                            sourceAttributeId={row.attribute_id}
                                                            targetAttributeId={row?.target_attribute_id}
                                                            payload={payload}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default SourceUniqueAttributesTable